/**
 * 「浏览与编辑」单表时**按录入时的行分组**（2026-10-20 / 迁移 0019）。
 *
 * ## 用户原话（验收标准）
 *
 * > "查一张表的时候，能不能**按我录入时的行来排**？一行一行地核对，
 * >  我才知道这个字原来属于第几行。"
 *
 * ## 什么时候才分组（三个条件**同时**成立）
 *
 *   1. **已生效的筛选解析出恰好一本书**（出版社 + 年级 + 学期三格齐，且那一册存在
 *      —— 就是 `findFilterVolume() !== null`）。三格没齐时列表可能是跨册的，
 *      行号没有共同含义（第 3 行是这本的第 3 行还是那本的第 3 行？）；
 *   2. **`listType` 是 `shizi` / `xiezi`**：词语表（`cibiao`）与「全部表」都不分组。
 *      全部表里识字表 / 写字表混在一起，同一行号会出现两次，分组只会更乱；
 *   3. **这次拿到的每一张卡都有行号**（`listingLineNo` 是 1 起的整数）。
 *      只要有一行是 `null`（迁移前的存量 / 手工新增的字），就**整块不分组** ——
 *      半组半不组的列表比连续网格更难核对（用户要的是"一行一行"，不是"大部分行"）。
 *
 * 三个条件任意一条不成立 ⇒ 返回 `null`，调用方**保持改造前的连续网格**（一个字节不改）。
 *
 * ## 复用哪一份分组逻辑
 *
 * `groupByLine()`（`importPreviewLineGroups.ts`）—— 与导入预览**同一份**实现：
 * 标题 `第 N 行（X 个字）`、组间按行号升序、组内按 `indexInLine` 升序。
 * 本文件只负责"要不要分组"这个判定 + 把 `HanziView[]` 翻译成 `LineGroupedItem`。
 *
 * ## 不分组时**不许**做的三件事
 *
 *   · 不许改后端排序（顺序仍是 `sort_order, created_at, id`）；
 *   · 不许丢字 / 去重（分组只是把它们分块，条数必须逐一相等）；
 *   · 不许改卡片本身（底色 / 已标音 / 点击 / 跳转 / 拼音全在 `HanziCard` 里，
 *     这里只决定"放进哪个 `<ul>`"）。
 */
import type { HanziListType } from '@fht/shared';
import type { HanziView } from '../../lib/api/dictation';
import { groupByLine, type LineGroup } from './importPreviewLineGroups';

/** 浏览页里一个"字卡位置"：卡片本身 + 它在录入原文里的行号 / 行内序号。 */
export interface BrowseLineItem {
  /** 这一张卡（原样交给 `HanziCard`，**一个字段都不改**）。 */
  item: HanziView;
  /**
   * 这一张卡在**当前筛选结果**里的下标（0 起）。
   *
   * ⚠️ 只用于"没有 `listingId` 的兜底跳转键"（`hanziRowKey(item, rowIndex)`）：
   *    同字多行时 `id` 相同，键里必须带一个"行"的区分，否则跳转/React key 会撞。
   *    分组会重排行，所以这里存的是**分组前**的下标，不是组内序号。
   */
  rowIndex: number;
  /** 原文行号（1 起）—— 来自 `hanzi_listing.line_no`。 */
  line: number;
  /** 这一行的第几个字（1 起）。列表本来就是 `sort_order` 序，所以就是它的行内位置。 */
  indexInLine: number;
}

/** 判定"要不要分组"需要的三样东西（全部来自**已生效**的筛选 + 列表数据）。 */
export interface BrowseLineGroupingInput {
  /**
   * `findFilterVolume(textbooks, appliedFilter)` 的结果。
   * `null` = 三格没齐 / 这一册不存在 / 课本列表没拿到 —— 三种情况都**不分组**。
   */
  volume: unknown | null;
  /** 已生效的「在哪张表上」（空串 = 全部表）。 */
  listType: string;
  /** 这次拿到的字卡（顺序 = 后端给的顺序，**不重排**）。 */
  items: readonly HanziView[];
}

/** 这个位置的行号（不是 1 起的整数 ⇒ `null`，也就是"这一行没记过"）。 */
function lineNoOf(item: HanziView): number | null {
  const value = item.listingLineNo;
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : null;
}

/** 只有识字表 / 写字表才谈得上"按行分组"（词语表是另一块界面，全部表会混号）。 */
function isSingleHanziTable(listType: string): listType is HanziListType {
  return listType === 'shizi' || listType === 'xiezi';
}

/**
 * 单表 + 每张卡都有行号 ⇒ 按行分组；否则 `null`（= 保持连续网格）。
 *
 * ⚠️ 返回值里的 `positions` 就是 `BrowseLineItem`：组内顺序 = 它在列表里的先后
 *    （= `sort_order`），条数与入参**逐一相等**（不丢、不合并）。
 */
export function groupBrowseHanziByLine(
  input: BrowseLineGroupingInput,
): LineGroup<BrowseLineItem>[] | null {
  if (input.volume === null) return null;
  if (!isSingleHanziTable(input.listType)) return null;
  if (input.items.length === 0) return null;

  /** 先整体检查一遍"是不是每一张卡都有行号" —— 有一张没有就整块不分组。 */
  const rows: BrowseLineItem[] = [];
  const indexInLine = new Map<number, number>();
  input.items.forEach((item, rowIndex) => {
    const line = lineNoOf(item);
    if (line === null) return;
    const next = (indexInLine.get(line) ?? 0) + 1;
    indexInLine.set(line, next);
    rows.push({ item, rowIndex, line, indexInLine: next });
  });
  /** 上面用 `forEach` 没法提前退出：这里补一次"有没有缺行号"的判定。 */
  if (rows.length !== input.items.length) return null;

  /**
   * ⚠️ 传 `listType`：单表筛选下它恒为同一张表，所以「混表加表名」那条分支
   *    不会触发（标题就是 `第 N 行（X 个字）`，与导入预览**逐字相同**）。
   */
  return groupByLine(
    rows.map((row) => ({
      item: row.item,
      rowIndex: row.rowIndex,
      line: row.line,
      indexInLine: row.indexInLine,
      listType: input.listType as HanziListType,
    })),
  );
}

/** 分组上方那句说明（`null` = 没分组，不该说）。 */
export function describeBrowseLineGroups(
  groups: readonly LineGroup<BrowseLineItem>[] | null,
): string | null {
  if (groups === null) return null;
  const count = groups.reduce((sum, group) => sum + group.positions.length, 0);
  return (
    `按你录入时的行分组：共 ${groups.length} 行、${count} 个字` +
    '（每块就是录入时的一行；手机放不下时会在同一块里自动折行）'
  );
}
